import { observer } from 'mobx-react-lite';
import { useContext, type KeyboardEvent, type ReactNode } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { Button, XStack, YStack } from '@/components/ui';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { WidgetContext, useWidgetContext } from './widget-context';
import { useWidgetDisplay } from './widget-display.context';
import { WidgetDragContext } from './widget-drag-context';

type WidgetRootProps = {
  id: string;
  collapsible?: boolean;
  className?: string;
  children: ReactNode;
};

const WidgetRoot = observer(
  ({ id, collapsible = true, className, children }: WidgetRootProps) => {
    const { configStore, mode } = useWidgetDisplay();
    const collapsed = collapsible && configStore.isCollapsed(id);
    const toggle = () => {
      if (!collapsible) return;
      const next = !collapsed;
      trackEvent(
        mode === 'home'
          ? 'HOME_SECTION_COLLAPSE_TOGGLED'
          : 'WIDGETS_COLLAPSE_TOGGLED',
        {
          widget_id: id,
          collapsed: next,
        }
      );
      configStore.setCollapsed(id, next);
    };

    return (
      <WidgetContext.Provider value={{ id, collapsed, collapsible, toggle }}>
        <YStack
          className={cn(
            'group/widget shrink-0',
            mode === 'card' &&
              'overflow-hidden rounded-2xl border border-border-strong bg-surface shadow-sm',
            mode === 'home' && '[--ac-row-end-inset:calc(0.625rem+1px)]',
            className
          )}
          role="region"
          aria-labelledby={`widget-${id}-title`}
        >
          {children}
        </YStack>
      </WidgetContext.Provider>
    );
  }
);

type WidgetHeaderProps = {
  title: string;
  count?: ReactNode;
  titleAccessory?: ReactNode;
  titleClusterClassName?: string;
  accessory?: ReactNode;
  className?: string;
};

const WidgetHeader = observer(
  ({
    title,
    count,
    titleAccessory,
    titleClusterClassName,
    accessory,
    className,
  }: WidgetHeaderProps) => {
    const { id, collapsed, collapsible, toggle } = useWidgetContext();
    const { mode } = useWidgetDisplay();
    const drag = useContext(WidgetDragContext);

    const chevron = (
      <Icons.chevronDown
        className={cn(
          mode === 'home' ? 'size-3.5' : 'mr-0.5 size-3',
          'text-foreground-subtle transition-[transform,opacity]',
          collapsed && '-rotate-90',
          mode === 'home' &&
            !collapsed &&
            'opacity-0 group-hover/widget-header:opacity-100 group-has-[:focus-visible]/widget-header:opacity-100'
        )}
        strokeWidth={2}
      />
    );

    const titleCluster = (
      <>
        {collapsible && mode === 'card' && chevron}
        <span
          id={`widget-${id}-title`}
          className={
            mode === 'home'
              ? 'font-serif text-lg text-foreground'
              : 'text-xs font-medium text-foreground-muted'
          }
        >
          {title}
        </span>
        {count !== undefined && (
          <span
            className={
              mode === 'home'
                ? 'ml-1 text-sm text-foreground-muted'
                : 'text-xs text-foreground-subtle'
            }
          >
            {count}
          </span>
        )}
        {collapsible && mode === 'home' && chevron}
      </>
    );

    return (
      <XStack
        ref={drag?.setActivatorNodeRef}
        {...(drag?.listeners ?? {})}
        // Default cursor even when draggable, like a window title bar.
        className={cn(
          'group/widget-header items-center gap-2 cursor-default',
          mode === 'card' ? 'px-4 py-2.5' : 'h-10 px-2',
          className
        )}
      >
        <XStack
          className={cn(
            'min-w-0 flex-1 items-center gap-1.5 self-stretch',
            titleClusterClassName
          )}
        >
          {collapsible ? (
            <button
              type="button"
              aria-expanded={!collapsed}
              onClick={toggle}
              className="focus-ring flex min-w-0 flex-1 cursor-default items-center gap-1.5 self-stretch rounded-md text-left"
            >
              {titleCluster}
            </button>
          ) : (
            titleCluster
          )}
          {titleAccessory && (
            <span
              className="ml-auto"
              onPointerDown={(e) => e.stopPropagation()}
            >
              {titleAccessory}
            </span>
          )}
        </XStack>
        {accessory && (
          <span
            className="ml-auto"
            // Keep the drag handle off interactive accessories.
            onPointerDown={(e) => e.stopPropagation()}
          >
            {accessory}
          </span>
        )}
      </XStack>
    );
  }
);

type WidgetBodyProps = {
  className?: string;
  keepMounted?: boolean;
  children: ReactNode;
};

const WidgetBody = observer(
  ({ className, keepMounted = false, children }: WidgetBodyProps) => {
    const { collapsed } = useWidgetContext();
    const { mode } = useWidgetDisplay();
    if (collapsed && !keepMounted) return null;
    if (mode === 'card') {
      return (
        <div
          hidden={collapsed}
          className={cn('px-2 pb-2', className, collapsed && 'hidden')}
        >
          {children}
        </div>
      );
    }
    return (
      <YStack
        hidden={collapsed}
        className={cn('px-0', className, collapsed && 'hidden')}
      >
        {children}
      </YStack>
    );
  }
);

type WidgetRowProps = {
  onClick?: () => void;
  align?: 'start' | 'center';
  className?: string;
  children: ReactNode;
};

const WidgetRow = ({
  onClick,
  align = 'start',
  className,
  children,
}: WidgetRowProps) => {
  const interactive = Boolean(onClick);
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={cn(
        'flex gap-3 px-2 py-1.5 rounded-md',
        align === 'center' ? 'items-center' : 'items-start',
        interactive && 'hover:bg-surface-hover cursor-default',
        className
      )}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
    >
      {children}
    </div>
  );
};

type WidgetEmptyProps = {
  children?: ReactNode;
  icon?: LBIcon;
  title?: string;
  action?: () => void;
};

const WidgetEmpty = ({
  children,
  icon: Icon,
  title,
  action,
}: WidgetEmptyProps) => {
  if (!Icon && !title && !action) {
    return (
      <span className="block text-xs text-foreground-subtle py-1 px-2">
        {children}
      </span>
    );
  }

  const body = (
    <>
      {Icon && (
        <span className="inline-flex items-center justify-center size-7 rounded-md shrink-0 bg-surface-active text-foreground-muted">
          <Icon className="size-3.5" strokeWidth={2} />
        </span>
      )}
      <YStack className="gap-0.5 min-w-0 flex-1">
        {title && (
          <span className="text-sm font-medium text-foreground">{title}</span>
        )}
        {children && (
          <span className="text-xs text-foreground-muted leading-snug">
            {children}
          </span>
        )}
      </YStack>
    </>
  );

  if (action) {
    return (
      <button
        type="button"
        onClick={action}
        className="flex w-full items-start gap-3 rounded-md px-2 py-2 text-left cursor-default hover:bg-surface-hover"
      >
        {body}
      </button>
    );
  }

  return <XStack className="items-start gap-3 px-2 py-2">{body}</XStack>;
};

type WidgetHeaderButtonProps = {
  readonly onClick: () => void;
  readonly children: ReactNode;
};

const WidgetHeaderButton = ({ onClick, children }: WidgetHeaderButtonProps) => (
  <Button
    variant="outline"
    size="xs"
    textSize="xs"
    className="h-[1.625rem] rounded-full bg-background px-2.5 text-sm font-medium text-foreground"
    onClick={onClick}
  >
    {children}
  </Button>
);

const WidgetSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <YStack className="gap-2 px-2 py-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="h-9 w-full rounded-md bg-surface-active opacity-30 animate-pulse"
      />
    ))}
  </YStack>
);

export const Widget = Object.assign(WidgetRoot, {
  Header: WidgetHeader,
  HeaderButton: WidgetHeaderButton,
  Body: WidgetBody,
  Row: WidgetRow,
  Empty: WidgetEmpty,
  Skeleton: WidgetSkeleton,
});
